import { defineComponent as _defineComponent } from 'vue'
import Fuse from 'fuse.js'
import { computed, nextTick, provide, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { nanoid } from 'nanoid'

import { useCommandStore } from './useCommandStore'
import { ItemInfo } from './types'
import { optionsInjectionKey } from './utils'


export default /*#__PURE__*/_defineComponent({
  __name: 'Command',
  props: {
    shouldFilter: { type: Boolean, default: true },
    storeId: { default: nanoid() }
  },
  setup(__props: any, { expose }) {

const props = __props as { shouldFilter: boolean, storeId: string };

const ITEM_SELECTOR = '[cmd-item=""]'
const VALID_ITEM_SELECTOR = `${ITEM_SELECTOR}:not([aria-disabled="true"])`
const SELECTED_ITEM_SELECTOR = `${ITEM_SELECTOR}[aria-selected="true"]`
const SELECT_EVENT = `cmd-item-select`
const VALUE_ATTR = `data-value`



expose({
  onKeyDown,
})

provide(optionsInjectionKey, {
  shouldFilter: props.shouldFilter,
  storeId: props.storeId,
})

const commandStore = useCommandStore(props.storeId)
const { value, search, items, filtered } = storeToRefs(commandStore)

const commandRef = ref<HTMLDivElement>()

const fuse = computed(() => {
  return new Fuse(
    items.value.filter((item) => !item.excludeFromFilter),
    { keys: ['label', 'tags'], threshold: 0.2 },
  )
})

watch(search, () => {
  filterItems()
  nextTick(selectFirstItem)
})

/**
 * Filters the current items.
 */
const filterItems = () => {
  if (!search.value) {
    filtered.value.count = items.value.length
    filtered.value.items = []
    filtered.value.groups = []
    // Do nothing, each item will know to show itself because search is empty
    return
  }

  const filteredItems: ItemInfo[] = fuse.value
    .search(search.value)
    .map((r) => r.item)

  const filteredItemIds = new Set<string>()
  const filteredGroupIds = new Set<string>()
  for (const { itemId, groupId } of filteredItems) {
    filteredItemIds.add(itemId)
    filteredGroupIds.add(groupId)
  }

  filtered.value.items = Array.from(filteredItemIds)
  filtered.value.count = filtered.value.items.length
  filtered.value.groups = Array.from(filteredGroupIds)
}

function onKeyDown(e: KeyboardEvent) {
  switch (e.key) {
    case 'ArrowDown': {
      e.preventDefault()
      updateSelectedByDirection(1)
      break
    }
    case 'ArrowUp': {
      e.preventDefault()
      updateSelectedByDirection(-1)
      break
    }
    case 'Enter': {
      if (!e.isComposing) {
        e.preventDefault()
        const item = getSelectedItem()
        if (item) {
          const event = new Event(SELECT_EVENT)
          item.dispatchEvent(event)
        }
      }
    }
  }
}

const getSelectedItem = () => {
  return commandRef.value?.querySelector(SELECTED_ITEM_SELECTOR)
}

const getValidItems = (
  rootNode: HTMLElement | undefined = commandRef.value,
) => {
  const allItemEl = rootNode?.querySelectorAll(
    VALID_ITEM_SELECTOR,
  ) as NodeListOf<HTMLElement>
  return allItemEl ? Array.from(allItemEl) : []
}

function selectFirstItem() {
  const [firstItem] = getValidItems()
  if (firstItem) {
    value.value = firstItem.getAttribute(VALUE_ATTR) ?? ''
  }
}

function updateSelectedByDirection(direction: 1 | -1) {
  const selected = getSelectedItem()
  const items = getValidItems()
  const index = items.findIndex((item) => item === selected)

  // Get item at this index
  const newSelected = items[index + direction]
  if (newSelected) {
    value.value = newSelected.getAttribute(VALUE_ATTR) ?? ''
  }
}

return { __sfc: true,ITEM_SELECTOR, VALID_ITEM_SELECTOR, SELECTED_ITEM_SELECTOR, SELECT_EVENT, VALUE_ATTR, props, commandStore, value, search, items, filtered, commandRef, fuse, filterItems, onKeyDown, getSelectedItem, getValidItems, selectFirstItem, updateSelectedByDirection }
}

})